/**
 * StepHostSelection - Existing host selection panel.
 *
 * Shown when enable_existing_host_selection is true and we have a
 * consumer email before reaching the host step. Allows the user to
 * pick an existing host (pre-filling hostAttributeValues) or choose
 * "Register a new {host}" to proceed to the normal host form.
 */

import { useMemo } from 'react'
import { Check, Plus, Car, Home, Ship, Package, Loader2 } from 'lucide-react'
import { useConsumerHosts, type ConsumerHostSummary } from '@/admin/hooks/use-host-templates'
import type { WizardState } from '@/admin/pages/registrations/new-registration-page'
import type { HostTemplate } from '@/admin/types/hosts'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface StepHostSelectionProps {
  state: WizardState
  update: (payload: Partial<WizardState>) => void
  hostTemplate: HostTemplate
  onSelectExisting: (host: ConsumerHostSummary) => void
  onCreateNew: () => void
}

// ---------------------------------------------------------------------------
// Icon resolver (mirrors step-host-product)
// ---------------------------------------------------------------------------

function HostIcon({ icon, size, className }: { icon: string; size: number; className?: string }) {
  switch (icon) {
    case 'car':
      return <Car size={size} className={className} />
    case 'home':
    case 'house':
      return <Home size={size} className={className} />
    case 'ship':
    case 'boat':
    case 'anchor':
      return <Ship size={size} className={className} />
    default:
      return <Package size={size} className={className} />
  }
}

// ---------------------------------------------------------------------------
// Host card
// ---------------------------------------------------------------------------

function HostCard({
  host,
  selected,
  onClick,
}: {
  host: ConsumerHostSummary
  selected: boolean
  onClick: () => void
}) {
  // Pick up to 3 most important attribute values to display
  const displayAttrs = useMemo(() => {
    return (host.attribute_values ?? []).slice(0, 3)
  }, [host.attribute_values])

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-[8px] border-[0.5px] px-4 py-[14px] text-left transition-all duration-150 ${
        selected
          ? 'border-[var(--green)] border-l-2 bg-[var(--green-bg)]'
          : 'border-[var(--rule)] border-l-2 border-l-transparent bg-[var(--bg)] hover:border-[var(--green-border)] hover:bg-[var(--bg-subtle)]'
      }`}
    >
      {/* Icon */}
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] transition-all duration-150 ${
          selected
            ? 'bg-[var(--green)] text-white'
            : 'border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-3)]'
        }`}
      >
        <HostIcon
          icon={host.host_template?.icon ?? 'package'}
          size={16}
          className="text-inherit"
        />
      </div>
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={`truncate whitespace-nowrap text-[13px] text-[var(--ink)] ${
              selected ? 'font-light' : ""
            }`}
          >
            {host.name}
          </span>
          {host.registration_number && (
            <span
              className="shrink-0 rounded-[3px] bg-[var(--bg-subtle)] px-1.5 py-[2px] font-mono text-[10px] font-light uppercase tracking-[0.06em] text-[var(--ink-3)]"
            >
              {host.registration_number}
            </span>
          )}
        </div>
        {displayAttrs.length > 0 && (
          <p
            className="mt-0.5 truncate whitespace-nowrap text-[11px] text-[var(--ink-3)]"
          >
            {displayAttrs
              .filter((a) => a.value != null && a.value !== '')
              .map((a) => String(a.value))
              .join(' \u00b7 ')}
          </p>
        )}
      </div>
      {/* Selection indicator */}
      {selected && (
        <div
          className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-[var(--green)]"
        >
          <Check size={12} className="text-white" />
        </div>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// "Create new" card
// ---------------------------------------------------------------------------

function CreateNewCard({
  hostTemplateName,
  selected,
  onClick,
}: {
  hostTemplateName: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-[8px] border-[0.5px] px-4 py-[14px] text-left transition-all duration-150 ${
        selected
          ? 'border-[var(--green)] bg-[var(--green-bg)]'
          : 'border-dashed border-[var(--rule-strong)] bg-[var(--bg)] hover:border-[var(--green-border)] hover:bg-[var(--bg-subtle)]'
      }`}
    >
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] transition-all duration-150 ${
          selected
            ? 'bg-[var(--green)] text-white'
            : 'border-[0.5px] border-dashed border-[var(--rule-strong)] bg-transparent text-[var(--ink-3)]'
        }`}
      >
        <Plus size={16} className="text-inherit" />
      </div>
      <span
        className={`text-[13px] font-display transition-colors duration-150 ${
          selected ? 'font-light text-[var(--ink)]' : "text-[var(--ink-3)]"
        }`}
      >
        Register a new {hostTemplateName.toLowerCase()}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StepHostSelection({
  state,
  update,
  hostTemplate,
  onSelectExisting,
  onCreateNew,
}: StepHostSelectionProps) {
  const { data: hosts, isLoading } = useConsumerHosts(
    state.consumerEmail || null,
    hostTemplate.uuid,
  )

  const selectedUuid = state.selectedExistingHostUuid

  const handleSelectHost = (host: ConsumerHostSummary) => {
    // Build hostAttributeValues from the host's attribute_values
    const values: Record<string, unknown> = {}
    for (const attr of host.attribute_values ?? []) {
      values[attr.code_name] = attr.value
    }
    update({
      selectedExistingHostUuid: host.uuid,
      hostAttributeValues: values,
    })
    onSelectExisting(host)
  }

  const handleCreateNew = () => {
    update({
      selectedExistingHostUuid: null,
      hostAttributeValues: {},
    })
    onCreateNew()
  }

  // If no hosts found and not loading, auto-proceed to create new
  const hasHosts = (hosts ?? []).length > 0

  return (
    <div
      className="panel-arrive max-w-[520px] rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5"
    >
      <p className="mb-4 text-[10px] font-light uppercase tracking-[0.16em] text-[var(--ink-3)]">
        Select a {hostTemplate.name}
      </p>
      {isLoading ? (
        <div className="py-8 flex flex-col items-center gap-3">
          <Loader2 size={20} className="animate-spin text-[var(--green)]" />
          <p className="text-[12px] text-[var(--ink-3)]">
            Looking up existing {hostTemplate.name.toLowerCase()}s...
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {hasHosts && (
            <>
              {hosts!.map((host) => (
                <HostCard
                  key={host.uuid}
                  host={host}
                  selected={selectedUuid === host.uuid}
                  onClick={() => handleSelectHost(host)}
                />
              ))}
            </>
          )}

          <CreateNewCard
            hostTemplateName={hostTemplate.name}
            selected={selectedUuid === 'new'}
            onClick={handleCreateNew}
          />
        </div>
      )}
    </div>
  );
}
